import { Button, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useAdvisorRvLinks, useOwnUserNode, useUpdateUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import type { Grower } from '/@/types/growers';
import RVOnboardingModalAdvisorSelector from '/@/views/dashboard/rv/components/onboarding-modal-and-alert/advisor-selector';

const AssignAdvisorModal = ({
    open,
    setOpen,
    grower,
    selectedAdvisorId,
    setSelectedAdvisorId,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    grower: Grower;
    selectedAdvisorId: string;
    setSelectedAdvisorId: (selectedAdvisorId: string) => void;
}) => {
    const [openRVAdvisorLinkModal, setOpenRVAdvisorLinkModal] = useState(false);
    const ownUserNodeQuery = useOwnUserNode();
    const updateUserNodeMutation = useUpdateUserNode();

    const RVAdvisorLinksQuery = useAdvisorRvLinks();
    const advisorOptions = useMemo(() => {
        return (RVAdvisorLinksQuery.data?.results || []).map((link) => ({
            value: link.advisor.id.toString(),
            label: link.advisor.name,
        }));
    }, [RVAdvisorLinksQuery.data]);

    const handleAssignAdvisor = useCallback(async () => {
        const rvId = ownUserNodeQuery.data?.id;
        if (!rvId || !selectedAdvisorId) return;
        try {
            await updateUserNodeMutation.trigger({
                id: grower.id,
                data: { parents: [rvId, Number(selectedAdvisorId)] },
            });
            setOpen(false);
            setSelectedAdvisorId('');
        } catch {
            // Error is handled by the mutation / API
        }
    }, [
        ownUserNodeQuery.data?.id,
        selectedAdvisorId,
        grower.id,
        updateUserNodeMutation,
        setOpen,
        setSelectedAdvisorId,
    ]);

    return (
        <>
            <ModalV2
                open={open}
                onClose={() => {
                    setOpen(false);
                    setSelectedAdvisorId('');
                }}
                title={_('Asignar Asesor')}
                actions={
                    advisorOptions.length > 0 ? (
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAssignAdvisor();
                            }}
                            disabled={!selectedAdvisorId || updateUserNodeMutation.isMutating}
                        >
                            {_('Asignar')}
                        </Button>
                    ) : (
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpenRVAdvisorLinkModal(true);
                                setOpen(false);
                            }}
                        >
                            {_('Vincularme con Asesores')}
                        </Button>
                    )
                }
                actionsProps={{
                    justifyContent: 'flex-end',
                }}
            >
                {advisorOptions.length > 0 ? (
                    <MuiSelect
                        inputLabel={_('Asesor')}
                        value={selectedAdvisorId}
                        onChange={(e) => {
                            e.stopPropagation();
                            setSelectedAdvisorId(String(e.target.value ?? ''));
                        }}
                        options={[{ value: '', label: _('Seleccionar asesor') }, ...advisorOptions]}
                        formControlProps={{ fullWidth: true, size: 'small' }}
                        inputProps={{ sx: { background: 'white' } }}
                    />
                ) : (
                    <>
                        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                            {_('No tenés Asesores vinculados')}
                        </Typography>
                        <Typography variant='body1' color='primary'>
                            {_('Vinculate con Asesores para poder asignarlos a tus productores.')}
                        </Typography>
                    </>
                )}
            </ModalV2>
            <RVOnboardingModalAdvisorSelector open={openRVAdvisorLinkModal} setOpen={setOpenRVAdvisorLinkModal} />
        </>
    );
};

export default AssignAdvisorModal;
